import { useState, useEffect, useRef } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import CustomStyledTable from "../../../../../components/table/CustomStyledTable";
import { Columns } from "./data";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import TaxReliefTypeModalForm from "./modal";
import ActionPopover from "../../../../../components/popovers/actionPopover";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import {
	useDeleteTaxReliefType,
	useGetTaxReliefType,
	useGetTaxReliefTypeSearch,
} from "../../../../../services/tax-relief-type.service";
import useEditFormStore from "../../../../../state-management/useEditFormStore";
import { useGetCountry } from "../../../../../services/country.service";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../helpers/useManageTable";
import { TaxReliefTypeDto } from "../../../../../models/tax-relief-type.dto";
import { APP_CONFIG } from "../../../../../helpers/appHelpers";
import { CountryOrderBy } from "../../../../../models/country.dto";
import TableFilter from "../../../../../components/table/filter/TableFilter";
import APISearchInput from "../../../../../components/search/APISearchInput";
import { TaxReliefTypesColumns } from "./tanstack-columns";
import TanStackEmployeeTable from "../../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../../models/table-configuration.dto";
import useUserStore from "../../../../../state-management/useUserStore";

const Types = () => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditTaxReliefTypeForm, setIsEditTaxReliefTypeForm] = useState(false);
	const currentUser = useUserStore().currentUser.user;

	const { mutate: searchTaxReliefTypes } = useGetTaxReliefTypeSearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<TaxReliefTypeDto>({
		fetchHook: useGetTaxReliefType,
	});

	// Fetched data for select fields requiring data from database
	const { data: countries } = useGetCountry({
		active: true,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: CountryOrderBy.NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditTaxReliefTypeForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteTaxReliefType } = useDeleteTaxReliefType();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteTaxReliefType(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];
	const columnsWithAdditions = Columns.map(
		(obj) => additionalColumns.find((o) => o.field === obj.field) || obj,
	);

	return (
		<div>
			<h2 className="text-2sm font-semibold mb-3">Tax Relief Types</h2>
			<hr />
			<div className="flex justify-between items-center mt-7 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchTaxReliefTypes}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Name",
										fieldName: "name",
									},
									{
										title: "Code",
										fieldName: "code",
									},
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
									{
										title: "Date Modified",
										fieldName: "modifiedAt",
									},
								],
							},
							{
								title: "Country",
								fieldName: "countryId",
								fieldType: "SELECT_FIELD",
								options: countries?.data || [],
							},
						]}
					/>
				</div>
				<div
					className="flex items-center text-primary-blue text-sm cursor-pointer"
					onClick={handleOpenModal}
				>
					<AiOutlinePlus className="mr-1 text-sm" />
					Add New Tax Relief Type
				</div>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.TAX_RELIEF_TYPES}
				data={tableData}
				columns={TaxReliefTypesColumns}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={setAnchorEl}
			/> */}
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditTaxReliefTypeForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<TaxReliefTypeModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={
						isEditTaxReliefTypeForm
							? "Update Tax Relief Type"
							: "Add New Tax Relief Type"
					}
					isEditForm={isEditTaxReliefTypeForm}
					tableAPIRef={tableAPIRef}
					countries={countries?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
		</div>
	);
};

export default Types;
